import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { AlertCircle } from 'lucide-react';
import { AccessRequestOverlay } from '../access-request-overlay';
import type { StreamStatus } from '@/lib/shared-types/constants';

interface StreamOverlaysProps {
  hasAccess: boolean;
  streamId: number;
  streamTitle: string;
  streamStatus: StreamStatus;
  sourceType?: 'live' | 'upload';
  scheduledStart?: string;
  creator?: { name: string };
  tags?: string[];
  duration?: number;
  viewerCount?: number;
  likes?: number;
  isLoading: boolean;
  error: string | null;
  errorType: 'network' | 'playback' | 'auth' | 'unknown';
  isPending: boolean;
  pendingReason: string | null;
  isProcessing: boolean;
  processingReason: string | null;
  playbackUrl: string | null;
}

export function StreamOverlays({
  hasAccess,
  streamId,
  streamTitle,
  streamStatus,
  sourceType,
  scheduledStart,
  creator,
  tags,
  duration,
  viewerCount,
  likes,
  isLoading,
  error,
  errorType,
  isPending,
  pendingReason,
  isProcessing,
  processingReason,
  playbackUrl
}: StreamOverlaysProps) {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatReleaseCountdown = (dateString?: string) => {
    if (!dateString) return null;
    const diffMs = new Date(dateString).getTime() - Date.now();
    if (diffMs <= 0) return 'Releasing now';
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    if (hours > 0) return `Releases in ${hours}h ${minutes}m`;
    return `Releases in ${minutes}m`;
  };

  return (
    <>
      {/* Access Request Overlay */}
      {!hasAccess && (
        <AccessRequestOverlay streamId={streamId} streamTitle={streamTitle} />
      )}

      {/* Scheduled Stream Overlay */}
      {streamStatus === 'scheduled' && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/70">
          <div className="text-center text-white">
            <div className="text-6xl mb-4">⏰</div>
            <h3 className="text-xl font-semibold mb-2">Stream Scheduled</h3>
            <p className="text-sm opacity-90">
              {scheduledStart
                ? formatDate(scheduledStart)
                : 'Starting soon'
              }
            </p>
            {sourceType === 'upload' && (
              <p className="mt-2 text-xs opacity-80">
                {formatReleaseCountdown(scheduledStart) || 'Video release pending'}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Error Overlay */}
      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/80">
          <div className="text-center text-white p-6 max-w-md">
            <AlertCircle className={`h-12 w-12 mx-auto mb-4 ${errorType === 'network' ? 'text-warning' :
              errorType === 'auth' ? 'text-destructive' : 'text-warning'
              }`} />
            <h3 className="text-lg font-semibold mb-2">
              {errorType === 'network' ? 'Connection Problem' :
                errorType === 'auth' ? 'Access Denied' :
                  errorType === 'playback' ? 'Playback Error' : 'Error'}
            </h3>
            <p className="text-sm opacity-80 mb-4">{error}</p>
          </div>
        </div>
      )}

      {/* Loading Overlay */}
      {(() => {
        const usingCloudflarePlayer = playbackUrl && (
          playbackUrl.includes('cloudflarestream.com') ||
          playbackUrl.includes('videodelivery.net')
        );
        return isLoading && !error && !isPending && !isProcessing && !usingCloudflarePlayer;
      })() && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/50">
          <LoadingSpinner size="lg" />
        </div>
      )}

      {/* Pending Stream Overlay - Waiting for RTMP input */}
      {isPending && streamStatus === 'live' && (
        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-900 to-gray-800">
          <div className="text-center text-white p-8 max-w-md">
            <div className="relative mb-6">
              <div className="w-16 h-16 border-4 border-primary/30 rounded-full mx-auto" />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            </div>
            <h3 className="text-xl font-semibold mb-2">Stream Starting...</h3>
            <p className="text-sm opacity-80 mb-4">
              {pendingReason || 'Waiting for the broadcaster to start streaming'}
            </p>
            <div className="text-xs opacity-60">
              The stream will begin automatically once the broadcast starts.
            </div>
          </div>
        </div>
      )}

      {/* Recording Processing Overlay - Stream ended but recording not ready yet */}
      {isProcessing && streamStatus === 'ended' && (
        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-900 to-gray-800">
          <div className="text-center text-white p-8 max-w-md">
            <div className="relative mb-6">
              <div className="w-16 h-16 border-4 border-primary/30 rounded-full mx-auto" />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            </div>
            <h3 className="text-xl font-semibold mb-2">Processing Recording</h3>
            <p className="text-sm opacity-80 mb-4">
              {processingReason || 'The stream has ended and the recording is being processed.'}
            </p>
            <div className="text-xs opacity-60">
              This usually takes a few minutes. The video will be available for playback once processing is complete.
            </div>
          </div>
        </div>
      )}

      {/* Playback unavailable — live stream has no playback URL yet */}
      {!isLoading && !error && !playbackUrl && streamStatus === 'live' && !isPending && (
        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-900 to-gray-800">
          <div className="text-center text-white p-8 max-w-md">
            <div className="text-6xl mb-4">📡</div>
            <h3 className="text-xl font-semibold mb-2">Stream Starting</h3>
            <p className="text-sm opacity-80 mb-4">
              Waiting for the live stream to become available. Please try again in a moment.
            </p>
            <div className="text-xs opacity-60">
              {streamTitle}
            </div>
          </div>
        </div>
      )}

      {/* Stream Status Overlay */}
      {streamStatus === 'live' && !error && (
        <div className="absolute top-4 left-4">
          <div className="bg-destructive text-white px-2 py-1 rounded text-sm font-medium flex items-center gap-2">
            <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
            LIVE
          </div>
        </div>
      )}

      {/* Stream Info Overlay (Mobile) */}
      {/* This would be implemented as a sheet/drawer component */}
    </>
  );
}
